import { useState, useEffect, memo } from 'react';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import Zoom from '@mui/material/Zoom';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { useReduxFileStorage } from '@Hooks/reduxFileStorageHook/useReduxFileStorage';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import ErrorHandler from '@Root/ErrorHandler';
import '@Partials/_ComStyles.scss';
import './Comment.scss';

function Comment(props) {
    const [comment, setComment] = useState({});
    const [commentUser, setCommentUser] = useState({});
    const [commentLoading, setCommentLoading] = useState(true);
    const [file, setFile] = useState(null);

    const colorList = [
        '#2784B9', '#40c47d', '#FDB16C', '#b06060', '#f77474',
        '#6080b0', '#9a60b0', '#6cb060', '#b0ae60', '#65412b',
        '#cc9963', '#e1bd83', '#60748b', '#744700', '#c8d579',
        '#5f8fba', '#3e6073', '#8e6dc1', '#473785', '#9158a9',
        '#7f3f7a', '#583e84', '#073763', '#20124d', '#3d124d',
        '#64113f', '#780448', '#003e23', '#108764', '#1a4c3d',
        '#c8d77f', '#11888b', '#1a95bd', '#0d6c7b', '#25b56e',
        '#006e3d'
    ];

    const userInfoService = new UserInfoService();
    const errorHandler = new ErrorHandler();

    const { activeUserId } = useReduxActiveUser();
    const { currentFiles, downloadFileFromUrl } = useReduxFileStorage();

    const className = 'comment';

    useEffect(() => {
        renderData();
    }, [props]);

    const renderData = async () => {
        setCommentLoading(true);

        const commentObj = props.comment;

        setComment(commentObj || {});
        setFile(null);
        try {
            if (commentObj?.userId) {
                let user = await userInfoService.getUserById(commentObj.userId);

                if (user?.body) {
                    user = JSON.parse(user.body);
                    setCommentUser(user);
                }
            }
            if (commentObj?.type === 'File') {
                const fileObj = currentFiles?.[commentObj.fileId];
                if (fileObj) {
                    setFile(fileObj);
                }
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCommentLoading(false);
    };

    const handlePinComment = async () => {
        try {
            const commentObj = {
                ...comment
            };
            if (commentObj?.type === 'File') {
                commentObj.text = 'Attached File';
            }
            commentObj.pinnedComment = !commentObj.pinnedComment;
            setComment(commentObj);
        } catch (err) {
            errorHandler.handleError(err);
        }
    };

    const getInitials = (name) => {
        if (!name) return 'NA';
        const names = name
            .trim()
            .split(' ')
            .filter(Boolean);
        if (names.length === 1) {
            return names[0].substring(0, 2).toUpperCase();
        }
        return (
            names[0].substring(0, 1) +
            names[names.length - 1].substring(0, 1)
        ).toUpperCase();
    };

    const dataURLtoFile = (dataurl) => {
        if (!dataurl) return null;
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)?.[1];
        if (!mime) return null;
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], 'avatar', {
            type: mime
        });
    };

    const getAvatarColor = () => {
        const name = commentUser?.fullName || 'User';
        return colorList[
            name.charCodeAt(0) % colorList.length
        ];
    };

    const downloadAttachment = async () => {
        try {
            if (file?.publicUrl) {
                await downloadFileFromUrl(
                    file.publicUrl,
                    file.fileName
                );
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';

        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        }).format(new Date(timestamp));
    };

    const getUserName = () => {
        const fullName = commentUser?.fullName;
        if (!fullName) return 'Unknown User';
        const parts = fullName
            .split(' ')
            .filter(Boolean);
        if (parts.length === 1) {
            return parts[0];
        }
        return `${parts[0]} ${parts[parts.length - 1][0]}.`;
    };

    const renderAvatar = () => {
        if (commentUser?.photo) {
            const avatarFile = dataURLtoFile(commentUser.photo);
            if (avatarFile) {
                return (
                    <Avatar
                        src={URL.createObjectURL(avatarFile)}
                        className={`${className}__avatarImage`}
                    />
                );
            }
        }

        return (
            <Avatar
                className={`${className}__avatarImage`}
                sx={{
                    backgroundColor: getAvatarColor()
                }}
            >
                {getInitials(commentUser?.fullName)}
            </Avatar>
        );
    };

    const getFileIcon = () => {
        if (file?.fileType?.includes('image')) {
            return <ImageIcon />;
        }
        if (file?.fileType?.includes('video')) {
            return <VideoLibraryIcon />;
        }
        if (file?.fileType?.includes('pdf')) {
            return <PictureAsPdfIcon />;
        }
        return <InsertDriveFileIcon />;
    };

    const getFileTypeLabel = () => {
        if (file?.fileType?.includes('image')) return 'IMAGE';
        if (file?.fileType?.includes('video')) return 'VIDEO';
        if (file?.fileType?.includes('pdf')) return 'PDF';

        return 'FILE';
    };

    const isSelf = String(activeUserId) === String(commentUser?.id);

    if (commentLoading) {
        return (
            <Box className={`${className} ${className}--skeleton`}>
                <Skeleton
                    variant="circular"
                    width={38}
                    height={38}
                />

                <Box className={`${className}__skeletonContent`}>
                    <Skeleton
                        variant="text"
                        width={120}
                        height={20}
                    />

                    <Skeleton
                        variant="rounded"
                        width={260}
                        height={65}
                    />

                    <Skeleton
                        variant="text"
                        width={100}
                        height={18}
                    />
                </Box>
            </Box>
        );
    }

    return (
        <Box
            className={`
                ${className}
                ${isSelf ? `${className}--self` : ''}
                ${comment?.pinnedComment ? `${className}--pinned` : ''}
            `}
        >
            {/* Avatar */}
            <Box className={`${className}__avatar`}>
                {renderAvatar()}
            </Box>

            {/* Main content */}
            <Box className={`${className}__content`}>

                {/* Header */}
                <Box className={`${className}__header`}>
                    <Box className={`${className}__identity`}>
                        <Typography
                            className={`${className}__userName`}
                        >
                            {getUserName()}
                        </Typography>

                        {comment?.pinnedComment && (
                            <Box className={`${className}__pinnedBadge`}>
                                <PushPinRoundedIcon />
                                <span>Pinned</span>
                            </Box>
                        )}
                    </Box>

                    <Box className={`${className}__meta`}>
                        <Box className={`${className}__time`}>
                            <AccessTimeRoundedIcon />
                            <span>
                                {formatTime(comment?.timeStamp)}
                            </span>
                        </Box>

                        <Tooltip
                            title={
                                comment?.pinnedComment
                                    ? 'Unpin comment'
                                    : 'Pin comment'
                            }
                            TransitionComponent={Zoom}
                            arrow
                        >
                            <IconButton
                                size="small"
                                onClick={handlePinComment}
                                className={`
                                    ${className}__pinButton
                                    ${comment?.pinnedComment
                                        ? `${className}__pinButton--active`
                                        : ''
                                    }
                                `}
                            >
                                {comment?.pinnedComment ? (
                                    <PushPinRoundedIcon />
                                ) : (
                                    <PushPinOutlinedIcon />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Message */}
                <Box className={`${className}__bubble`}>

                    {comment?.type === 'File' ? (
                        <Box className={`${className}__attachment`}>

                            {/* Image */}
                            {file?.fileType?.includes('image') && (
                                <Box
                                    className={`${className}__preview`}
                                >
                                    <img
                                        src={file.publicUrl}
                                        alt={file.fileName}
                                        className={`${className}__image`}
                                    />
                                </Box>
                            )}

                            {/* Video */}
                            {file?.fileType?.includes('video') && (
                                <Box
                                    className={`${className}__preview`}
                                >
                                    <video
                                        controls
                                        src={file.publicUrl}
                                        className={`${className}__video`}
                                    />
                                </Box>
                            )}

                            {/* PDF */}
                            {file?.fileType?.includes('pdf') && (
                                <Box
                                    className={`${className}__preview ${className}__preview--pdf`}
                                >
                                    <object
                                        data={file.publicUrl}
                                        type="application/pdf"
                                        className={`${className}__pdf`}
                                        aria-label={file.fileName}
                                    />
                                </Box>
                            )}

                            {/* File information */}
                            <Box
                                className={`${className}__attachmentInfo`}
                            >
                                <Box
                                    className={`${className}__fileIcon`}
                                >
                                    {getFileIcon()}
                                </Box>

                                <Box
                                    className={`${className}__fileDetails`}
                                >
                                    <Tooltip
                                        title={file?.fileName || ''}
                                        arrow
                                    >
                                        <Typography
                                            className={`${className}__fileName`}
                                        >
                                            {file?.fileName ||
                                                'Attachment'}
                                        </Typography>
                                    </Tooltip>

                                    <Box
                                        className={`${className}__fileMeta`}
                                    >
                                        <span>
                                            {getFileTypeLabel()}
                                        </span>

                                        {file?.fileSize && (
                                            <>
                                                <i />
                                                <span>
                                                    {Number(
                                                        file.fileSize
                                                    ).toFixed(2)}{' '}
                                                    MB
                                                </span>
                                            </>
                                        )}
                                    </Box>
                                </Box>

                                <Tooltip
                                    title="Download attachment"
                                    arrow
                                >
                                    <IconButton
                                        size="small"
                                        onClick={downloadAttachment}
                                        className={`${className}__download`}
                                    >
                                        <DownloadIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    ) : (
                        <Typography
                            className={`${className}__text`}
                        >
                            {comment?.text}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export default memo(Comment, arePropsEqual);

function arePropsEqual(oldProps, newProps) {
    return (
        JSON.stringify(oldProps.comment) ===
            JSON.stringify(newProps.comment) &&
        oldProps?.index === newProps?.index
    );
}
